/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useContext, useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import {
    array, object, string, shape,
    bool,
} from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { Grid, useMediaQuery } from '@material-ui/core';
import ReactHtmlParser from 'react-html-parser';
import { Link } from 'react-router-dom';
import findProductsByPartNumber from '../../../../../gql/queries/findProductsByPartNumber';
import { getBaseCode } from '../../../../../helpers/tracking/product/dataLayerProductHelpers';
import PriceRange from '../../../GraphqlCategoryPage/Partials/GraphqlProductContainer/Partials/GraphqlSimpleProduct/DesktopSimpleProduct/PriceRange';
import { CrossMerch } from '../../../CrossMerchContainer/CrossMerchContainer';
import GraphqlFoodQuickViewContainer from './GraphqlFoodQuickView/GraphqlFoodQuickViewContainer';
import { trackEvent } from '../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { getFeatureFlag } from '../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { getIsBot, getUserSubmittedProductFilterZipcode } from '../../../../../../state/ducks/App/App-Selectors';
import DynamicPrice from './DynamicPrice';
import { getEnterpriseId } from '../../../../../../state/ducks/Member/ducks/EnterpriseId/EnterpriseId-Selectors';
import UpsellsSkeleton from '../../../GraphqlSkeletonComponents/UpsellsSkeleton';
import replaceSpecialChar from '../../../../../helpers/replaceSpecialChar';
import useIsPlaPage from '../../../../../helpers/hooks/useIsPlaPage';
import useIsDisplayChannel from '../../../../../helpers/hooks/useIsDisplayChannel';

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        background: theme.palette.upsellsBackground || '#e8f1ec',
        margin: '10px 0px',
        '@media (max-width: 600px)': {
            padding: '20px 15px',
        },
        '@media (max-width: 768px) and (min-width:600px)': {
            padding: '20px 18px',
        },
        '@media (max-width: 1024px) and (min-width:768px)': {
            padding: '20px 15px',
        },
        '@media (min-width: 1025px)': {
            padding: '20px',
        },
    },
    noWrap: {
        flexWrap: 'nowrap',
    },
    header: {
        margin: 0,
        marginBottom: '10px',
        fontFamily: theme.palette.upsellsHeaderFonts || 'inherit',
        fontStyle: 'normal',
        fontWeight: 'normal',
        fontSize: '24px',
        lineHeight: '28px',
        '@media (max-width: 600px)': {
            fontFamily: theme.palette.upsellsHeaderFonts || 'inherit',
            fontStyle: 'normal',
            fontWeight: 'normal',
            fontSize: '20px',
            lineHeight: '24px',
        },
        '@media (max-width: 1024px) and (min-width:768px)': {
            fontSize: '20px',
            lineHeight: '24px',
        },
    },
    card: {
        background: theme.palette.white || '#fff',
        height: '100%',
        '@media (max-width: 600px)': {
            padding: '15px',
        },
        '@media (max-width: 768px) and (min-width:600px)': {
            padding: '15px',
        },
        '@media (max-width: 1024px) and (min-width:768px)': {
            padding: '13px 2px 13px 13px',
        },
        '@media (min-width: 1025px)': {
            padding: '15px',
        },
    },
    cardImage: {
        maxWidth: '100%',
        '@media (max-width: 600px)': {
            maxWidth: '84px',
        },
        '@media (max-width: 768px) and (min-width:600px)': {
            maxWidth: '70px',
        },
        '@media (max-width: 1024px) and (min-width:768px)': {
            maxWidth: '101.4px', // as per mockups
        },
        '@media (min-width: 1025px)': {
            maxWidth: '103.4px', // as per mockups
        },
    },
    plaImageCard: {
        maxWidth: '70px!important',
    },
    section: {
        paddingLeft: '10px',
    },
    productName: {
        fontWeight: 'bold',
        overflow: 'hidden',
        lineHeight: '18px',
        color: theme.palette.upsellsTextColor || '#2F2F2F',
        textDecoration: 'none',
        textOverflow: 'ellipsis',
        display: '-webkit-box',
        '-webkit-box-orient': 'vertical',
        '-webkit-line-clamp': 2,
        '@media (max-width: 600px)': {
            fontSize: '16px',
        },
        '@media (max-width: 768px) and (min-width:600px)': {
            fontSize: '16px',
        },
        '@media (max-width: 1024px) and (min-width:768px)': {
            fontSize: '15px',
        },
        '@media (min-width: 1025px)': {
            fontSize: '16px',
        },
    },
    productSku: {
        fontStyle: 'normal',
        fontWeight: 'normal',
        fontSize: '12px',
        lineHeight: '14px',
        textTransform: 'capitalize',
        color: theme.palette.upsellsTextColor || '#2F2F2F',
        margin: '5px 0',
        '@media (max-width: 600px)': {
            margin: ' 3px 0px 8px 0px',
        },
    },
    productPrice: {
        fontStyle: 'normal',
        fontWeight: 'bold',
        fontSize: '16px',
        lineHeight: '18px',
        color: theme.palette.upsellsTextColor || '#2F2F2F',
        margin: '8px 0',
        '@media (max-width: 840px) and (min-width:600px)': {
            margin: '4px 0',
        },
    },
    productQuickView: {
        fontStyle: 'normal',
        fontWeight: 'normal',
        fontSize: '14px',
        lineHeight: '18px',
        textDecorationLine: 'underline',
        color: theme.palette.upsellsTextColor || '#2F2F2F',
        cursor: 'pointer',
        paddingTop: '0px',
    },
    shipmentMessage: {
        fontStyle: 'normal',
        fontWeight: 'normal',
        fontSize: '14px',
        lineHeight: '18px',
        textTransform: 'capitalize',
        color: theme.palette.upsellsTextColor || '#2F2F2F',
        textDecoration: 'none',
        '@media (max-width: 840px) and (min-width:600px)': {
            fontSize: '12px',
            lineHeight: '16px',
        },
    },
    shipmentMessageFuture: {
        fontStyle: 'normal',
        fontWeight: 'normal',
        fontSize: '14px',
        lineHeight: '18px',
        textTransform: 'capitalize',
        color: theme.palette.upsellsFutureAvailabilityColor || '#A2011A',
        textDecoration: 'none',
        '@media (max-width: 840px) and (min-width:600px)': {
            fontSize: '12px',
            lineHeight: '16px',
        },
    },
    desktopMessage: {
        display: 'flex',
        justifyContent: 'space-between',
        position: 'absolute',
        right: '10px',
        left: '10px',
        bottom: '4px',
        '@media (max-width: 768px) and (min-width:600px)': {
            right: '5px',
            left: '0px',
            position: 'relative',
        },
        alignItems: 'flex-end',
    },
    relativeBox: {
        position: 'relative',
        flex: 'auto',
        '@media (min-width: 1025px)': {
            minHeight: '110px',
        },
    },
    ppPla: {
        display: 'flex',
        justifyContent: 'space-between',
    },
    plaUpsellBox: {
        width: 'inherit',
    },
    upSellsRoot: {
        background: 'none',
        padding: '16px 0px 12px',
        margin: '0px',
        '& $card': {
            background: '#fafafa',
            padding: '20px 24px',
        },
        '& $section': {
            paddingLeft: '16px',
        },
        '& $cardImage': {
            maxWidth: '100%',
            '@media (max-width: 600px)': {
                maxWidth: '84px',
            },
            '@media (max-width: 768px) and (min-width:600px)': {
                maxWidth: '70px',
            },
            '@media (max-width: 1024px) and (min-width:768px)': {
                maxWidth: '76px', // as per mockups
            },
            '@media (min-width: 1025px)': {
                maxWidth: '76px', // as per mockups
            },
        },
        '& $productName': {
            '& a': {
                lineHeight: '20px',
                fontSize: '16px',
                margin: '0px 0px 4px',
                letterSpacing: '0.5px',
                fontWeight: '400',
                color: '#2f2f2f',
            },
        },
        '& $productPrice': {
            margin: '0px',
            '& > span': {
                fontSize: '18px',
                lineHeight: '20px',
                marginBottom: '4px',
                letterSpacing: '0.5px',
                display: 'flex',
            },
        },
        '& $productSku': {
            lineHeight: '20px',
            margin: '0px 0px 4px',
            letterSpacing: '0.5px',
        },
        '& $shipmentMessage': {
            lineHeight: '20px',
            marginBottom: '4px',
            letterSpacing: '0.5px',
            color: '#2f2f2f',
            fontSize: '12px',
        },
        '& $desktopMessage': {
            position: 'relative',
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
            left: '0px',
            bottom: '0px',
            letterSpacing: '0.5px',
        },
        '& $header': {
            lineHeight: '30px',
            margin: '0px 24px 16px 0px',
            letterSpacing: '0.5px',
        },
        [theme.breakpoints.down('600')]: {
            margin: '0px 16px 0px',
            '& $card': {
                padding: '20px 16px',
            },
            '& $header': {
                lineHeight: '26px',
                margin: '0px 0px 16px',
            },
        },
    },
    plaRedesignUpsells: {
        background: theme.palette.upsellsBackground || '#e8f1ec',
        padding: '18px',
        '& $upsellCard': {
            display: 'flex',
            flexWrap: 'nowrap',
            '@media (max-width: 600px)': {
                flexWrap: 'wrap',
            },
        },
        '@media (max-width: 1024px)': {
            margin: '20px 0px 0px',
        },
        '& $productPrice': {
            '@media (max-width: 1024px)': {
                flexDirection: 'column',
            },
        },
        '& $shipmentMessage': {
            fontSize: '14px',
        },
    },
    upsellCard: {},
    singleUpsell: {
        width: '50%',
        '@media (max-width: 600px)': {
            width: 'calc(100% + 16px)',
        },
    },
}));

const Upsells = ({
    upsells,
    brandName,
    location,
    product,
    brand,
    productUrl,
    renderSkuPrice,
    demandType,
    selectedSku,
    isPdpRedesignEnabled,
    isPlaRedesignEnabled,
}) => {
    const dispatch = useDispatch();
    const isPlaPage = useIsPlaPage();
    const [productSkuPrices, setProductSkuPrices] = useState([]);
    const isOptimizeScriptEnabled = useSelector((state) => getFeatureFlag('is-optimize-script-enabled')(state));
    const isDynamicPriceEnabled = useSelector((state) => getFeatureFlag('is-dynamic-pricing-enabled')(state));
    const isZipDynamicPriceEnabled = useSelector(getFeatureFlag('is-zip-dynamic-pricing-enabled'));
    const isPDPQuickViewModalEnabled = useSelector(getFeatureFlag('is-PDP-quickview-modal-enabled'));
    const userSubmittedProductFilterZipcode = useSelector(getUserSubmittedProductFilterZipcode);
    const isBot = useSelector((state) => getIsBot(state));
    const enterpriseId = useSelector((state) => getEnterpriseId(state));
    const [dynamicPriceIsLoading, setDynamicPriceLoading] = useState(true);
    const foodPlaRedesign = useSelector(getFeatureFlag('which-food-pla-design'));

    // declaring Dynamic price call instance
    const dynamicPriceInstance = new DynamicPrice(upsells, enterpriseId, false, selectedSku?.subscriptionsList?.type?.[0] || '');

    const {
        getRetailPrice, getSalePrice,
    } = dynamicPriceInstance;

    useEffect(() => {
        const upsellList = (upsells || []).map((item) => item.id);
        const eventParam = {
            eventCategory: 'PDP Upsell',
            eventAction: `New Upsell | ${selectedSku?.id} | Demand Type ${demandType}`,
            eventLabel: `Upsells ${upsellList?.join?.(',')}`,
        };
        dispatch(trackEvent(eventParam));
    }, []);

    useEffect(() => {
        if (isBot || !isDynamicPriceEnabled) {
            setProductSkuPrices([]);
            setDynamicPriceLoading(false);
            return;
        }
        if (isOptimizeScriptEnabled && isDynamicPriceEnabled) {
            return;
        }

        const useZipcode = isZipDynamicPriceEnabled;

        setDynamicPriceLoading(true);
        dynamicPriceInstance.call(useZipcode, userSubmittedProductFilterZipcode).then((productPrices) => {
            setProductSkuPrices(productPrices?.newPrices || []);
            setDynamicPriceLoading(false);
        }).catch(() => {
            setDynamicPriceLoading(false);
        });
    }, [
        isDynamicPriceEnabled,
        isOptimizeScriptEnabled,
        userSubmittedProductFilterZipcode,
    ]);
    const isDisplayChannel = useIsDisplayChannel();
    const isDesktop = useMediaQuery('(min-width:1023px)');
    const forceMobileLayout = (isPlaPage || isDisplayChannel) && foodPlaRedesign === 'three_column';
    const layoutBreakpoint  = {
        isNotStackedLayout: {
            isActive: !forceMobileLayout && useMediaQuery('(min-width: 1024px)'),
            gridLeft: 4,
            gridRight: 8,
        },
        desktop: {
            isActive: !forceMobileLayout && useMediaQuery('(max-width: 1023px) and (min-width:768px)'),
            gridLeft: 3,
            gridRight: 9,
        },
        tablet: {
            isActive: !forceMobileLayout && useMediaQuery('(max-width: 768px) and (min-width:600px)'),
            gridLeft: 3,
            gridRight: 9,
        },
        mobile: {
            isActive: forceMobileLayout || useMediaQuery('(max-width: 601px)'),
            gridLeft: 4,
            gridRight: 8,
        },
    };

    const classes = useStyles();
    const { presentation_family } = useContext(CrossMerch);
    const skus = upsells?.map?.((item) => item.id) || [];
    const query = findProductsByPartNumber(
        brandName,
        skus,
    );

    const { data, loading, error } = useQuery(query);
    if (loading && !data && !error) {
        return <UpsellsSkeleton />;
    }
    const upsellsData = data?.findProductByPartNumbers || [];

    if (upsellsData.length === 0) {
        return <UpsellsSkeleton />;
    }
    let cardContainerGrid = 12;
    if (layoutBreakpoint.isNotStackedLayout.isActive && upsellsData?.length !== 1) {
        cardContainerGrid = 6;
    }

    if (dynamicPriceIsLoading) {
        return <></>;
    }

    const generateCard = (upsell) => {
        const isShipNow = upsell?.availability?.deliveryMessage === 'AVAILABLE TO SHIP NOW';
        let updatedPrices = upsell.skuPriceRange;
        if (productSkuPrices.length > 0) {
            const dynamicPriceList = productSkuPrices?.find?.((item) => item?.id === upsell.id)?.prices || [];
            if (dynamicPriceList.length > 0) {
                updatedPrices = {
                    retail: [{ value: getRetailPrice(dynamicPriceList) }],
                    sale: [{ value: getSalePrice(dynamicPriceList) }],
                };
            }
        }
        let upsellUrl = upsell?.seo?.url || '';
        if (demandType && typeof upsellUrl === 'string') {
            upsellUrl = upsellUrl.indexOf('?') >= 0 ? `${upsellUrl}&demandType=${demandType}` : `${upsellUrl}?demandType=${demandType}`;
        }

        return (
            <Grid item xs={cardContainerGrid}>
                <div className={classes.card}>
                    <Grid container className={classes.noWrap}>
                        <Grid item>
                            <Link
                                onClick={() => {
                                    dispatch(trackEvent({
                                        eventCategory: 'PDP Upsell',
                                        eventAction: 'New Upsell clicked',
                                        eventLabel: upsell.id,
                                    }));
                                }}
                                to={upsellUrl}
                            ><img className={`${forceMobileLayout ? classes.plaImageCard : ''} ${classes.cardImage}`} src={`${upsell.image.path}${upsell.image.name}c.jpg?width=150&auto=webp`} alt={ReactHtmlParser(upsell.name)} />
                            </Link>
                        </Grid>
                        <Grid className={`${!forceMobileLayout ?  classes.relativeBox : classes.plaUpsellBox}`} item>
                            <div className={classes.section}>
                                <div className={classes.productName}>
                                    <Link
                                        onClick={() => {
                                            dispatch(trackEvent({
                                                eventCategory: 'PDP Upsell',
                                                eventAction: 'New Upsell clicked',
                                                eventLabel: upsell.id,
                                            }));
                                        }}
                                        className={classes.productName}
                                        to={upsellUrl}
                                    >{ReactHtmlParser(replaceSpecialChar(upsell.name, false))}
                                    </Link>
                                </div>
                                <div className={classes.productSku}>{getBaseCode(upsell.id, true)}</div>
                                <div className={`${forceMobileLayout ? classes.ppPla : ''} ${classes.productPrice}`}>
                                    <PriceRange
                                        presentationFamily={presentation_family}
                                        skuPriceRange={updatedPrices}
                                        isMobile={false}
                                        sisterBrandProduct={false}
                                    />
                                    {(((forceMobileLayout || isPdpRedesignEnabled) && !isPlaRedesignEnabled) || (isPlaRedesignEnabled && !isDesktop)) && (
                                        <div className={`available-deluxe-pla ${classes.shipmentMessage} ${!isShipNow ? classes.shipmentMessageFuture : ''}`}>{upsell?.availability?.deliveryMessage?.toLowerCase?.()}</div>
                                    )}
                                </div>

                                {!layoutBreakpoint.mobile.isActive  ? (
                                    <div className={classes.desktopMessage}>
                                        {isPDPQuickViewModalEnabled
                                        && (
                                            <div className={!layoutBreakpoint.mobile.isActive ? classes.productQuickView : ''}>
                                                <GraphqlFoodQuickViewContainer
                                                    classes={classes}
                                                    product={product}
                                                    selectedSku={upsell}
                                                    location={location}
                                                    productUrl={productUrl}
                                                    brand={brand}
                                                    renderSkuPrice={renderSkuPrice}
                                                    onClickTracking={{
                                                        eventCategory: 'PDP Upsell',
                                                        eventAction: 'New Upsell - Quick View',
                                                        eventLabel: upsell.id,
                                                    }}
                                                />
                                            </div>
                                        )}
                                        {(layoutBreakpoint.desktop.isActive && !isPdpRedesignEnabled) &&  (
                                            <div className={`${classes.shipmentMessage} ${!isShipNow ? classes.shipmentMessageFuture : ''}`}>{upsell?.availability?.deliveryMessage?.toLowerCase?.()}</div>
                                        )}
                                    </div>
                                )
                                    : (
                                        !forceMobileLayout && !isPdpRedesignEnabled && (<div className={`${classes.shipmentMessage} ${!isShipNow ? classes.shipmentMessageFuture : ''}`}>{upsell?.availability?.deliveryMessage?.toLowerCase?.()}</div>)
                                    )}

                            </div>
                        </Grid>
                    </Grid>
                    {isPlaRedesignEnabled && isDesktop && (
                        <div className={`available-deluxe-pla ${classes.shipmentMessage} ${!isShipNow ? classes.shipmentMessageFuture : ''}`}>{upsell?.availability?.deliveryMessage?.toLowerCase?.()}</div>
                    )}
                    {!isPdpRedesignEnabled && (
                        <Grid container>
                            <Grid item xs={12}>
                                {!layoutBreakpoint.mobile.isActive && !layoutBreakpoint.desktop.isActive && <div className={`${classes.shipmentMessage} ${!isShipNow ? classes.shipmentMessageFuture : ''}`}>{upsell?.availability?.deliveryMessage?.toLowerCase?.()}</div>}
                            </Grid>
                        </Grid>
                    ) }
                </div>
            </Grid>
        );
    };
    let rootContainerGrid = 12;
    if (upsellsData?.length === 1 && layoutBreakpoint.isNotStackedLayout.isActive) {
        rootContainerGrid = 6;
    }
    const upsellPla = upsellsData?.length > 1 ? classes.upsellCard : classes.singleUpsell;
    return (
        <>
            <Grid container>
                <Grid item xs={12} sm={12} md={rootContainerGrid} lg={rootContainerGrid} xl={rootContainerGrid} className={`${classes.root} ${isPdpRedesignEnabled ? classes.upSellsRoot : ''} ${isPlaRedesignEnabled ? classes.plaRedesignUpsells : ''}`}>
                    <Grid container>
                        <Grid item xs={12}>
                            <h3 className={classes.header}>Go Deluxe</h3>
                        </Grid>
                        <Grid item xs={12}>
                            <Grid justify="space-around" container spacing={3} className={isPlaRedesignEnabled ? upsellPla : ''}>
                                {upsellsData?.map?.((upsell) => generateCard(upsell))}
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
};

Upsells.propTypes = {
    upsells: array.isRequired,
    brandName: string.isRequired,
    location: object,
    product: object,
    brand: object,
    productUrl: string,
    renderSkuPrice: array,
    demandType: string,
    selectedSku: shape({
        id: string,
    }),
    isPdpRedesignEnabled: bool,
    isPlaRedesignEnabled: bool,
};

Upsells.defaultProps = {
    location: {},
    product: {},
    brand: {},
    productUrl: '',
    demandType: 'UP',
    renderSkuPrice: [],
    selectedSku: {},
    isPdpRedesignEnabled: false,
    isPlaRedesignEnabled: false,
};

export default Upsells;
